import FileResultsList from '@github-ui/code-view-shared/components/files-search/FileResultsList'
import {ReposHeaderRefSelector} from '@github-ui/code-view-shared/components/ReposHeaderRefSelector'
import {useFileQueryContext} from '@github-ui/code-view-shared/contexts/FileQueryContext'
import {useOpenPanel} from '@github-ui/code-view-shared/contexts/OpenPanelContext'
import {useUrlCreator} from '@github-ui/code-view-shared/hooks/use-url-creator'
import type {CollapseTreeFunction, DirectoryItem, ReposFileTreeData} from '@github-ui/code-view-types'
import type {Repository} from '@github-ui/current-repository'
import {useCurrentRepository} from '@github-ui/current-repository'
import {sendEvent} from '@github-ui/hydro-analytics'
import {repositoryTreePath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import type {RefInfo} from '@github-ui/repos-types'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {scrollIntoView} from '@primer/behaviors'
import {AlertFillIcon, ChevronLeftIcon, PlusIcon} from '@primer/octicons-react'
import {ButtonGroup, IconButton, Overlay, SplitPageLayout} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import React, {useMemo} from 'react'
import {createHtmlPortalNode, InPortal, OutPortal} from 'react-reverse-portal'

import FileTreeContext from '../contexts/FileTreeContext'
import {fetchTree} from '../loaders/fetch-tree'
import type {TreeItem} from '../types/tree-item'
import {FindFilesShortcut} from './FindFilesShortcut'
import styles from './ReposFileTreePane.module.css'
import {buildReposFileTree, ReposFileTreeView} from './ReposFileTreeView'
import SearchButton from './SearchButton'

export const TreeOverlayBreakpoint = ScreenSize.xxxlarge

export interface ReposFileTreePaneProps {
  clientOnlyFilePaths?: string[]
  collapseTree: CollapseTreeFunction
  showTree: boolean
  fileTree: ReposFileTreeData
  onItemSelected: () => void
  processingTime: number
  treeToggleElement?: React.ReactElement
  treeToggleRef: React.RefObject<HTMLButtonElement | null>
  searchBoxRef: React.RefObject<HTMLInputElement | null>
  repo: Repository
  path: string
  refInfo: RefInfo
  isFilePath: boolean
  foldersToFetch: string[]
  incompleteFileTree?: boolean
  id: string
  onFindFilesShortcut: () => void
  textAreaId: string
  getItemUrlOverride?: (item: DirectoryItem) => string
  sortDirectoryItems?: (items: Array<TreeItem<DirectoryItem>>) => void
  headerClassName?: string
  paneClassName?: string
  paneContentsClassName?: string
  treeContainerClassName?: string
  findFileWorkerPath?: string
  headerContent?: React.ReactElement
  getFileTrailingVisual?: (item: DirectoryItem) =>
    | {
        trailingVisual: React.ReactElement
        screenReaderText?: string
      }
    | undefined
  getFileIcon?: (item: DirectoryItem) => React.ReactElement | null
  showFindFile?: boolean
  directoryNavigateOnClick?: boolean
  showRefSelectorRow?: boolean
  paneResizable?: boolean
}

export function ReposFileTreePane({
  clientOnlyFilePaths,
  collapseTree,
  showTree,
  fileTree,
  treeToggleElement,
  treeToggleRef,
  onItemSelected,
  processingTime,
  searchBoxRef,
  repo,
  path,
  refInfo,
  isFilePath,
  foldersToFetch,
  incompleteFileTree,
  id,
  onFindFilesShortcut,
  textAreaId,
  getItemUrlOverride,
  headerClassName,
  paneClassName,
  paneContentsClassName,
  treeContainerClassName,
  findFileWorkerPath,
  headerContent,
  getFileTrailingVisual,
  getFileIcon,
  sortDirectoryItems,
  showFindFile = true,
  directoryNavigateOnClick = true,
  showRefSelectorRow = true,
  paneResizable = true,
}: ReposFileTreePaneProps) {
  if (showFindFile && findFileWorkerPath === undefined) {
    throw new Error('findFileWorkerPath must be provided when showFindFile is true')
  }

  const {openPanel} = useOpenPanel()
  const [treeLoading, setTreeLoading] = React.useState(foldersToFetch.length > 0)
  const [fetchError, setFetchError] = React.useState(false)
  const fetchedFolders = React.useRef<string[]>([])
  const pendingFetchCount = React.useRef(0)
  const scrollingRef = React.useRef<HTMLDivElement | null>(null)
  const ignoreNextScroll = React.useRef(false)
  const selectedElement = React.useRef<HTMLElement | null>(null)
  const {query} = useFileQueryContext()
  const queryRef = React.useRef(query)
  queryRef.current = query
  const {codeCenterOption} = useCodeViewOptions()
  const lastOpenPanel = React.useRef(openPanel)
  const {isServer: isSSR} = useRuntimeEnvironment()
  const {defaultBranch} = useCurrentRepository()
  const {getItemUrl} = useUrlCreator()
  const getTreeItemUrl = getItemUrlOverride || getItemUrl

  let initialRenderRootItems: Array<TreeItem<DirectoryItem>> = []
  const newKnownFolders = new Map<string, TreeItem<DirectoryItem>>()

  const hasDoneInitialRender = React.useRef(treeLoading)
  if (!hasDoneInitialRender.current && fileTree) {
    const newKnownRootItems: Array<TreeItem<DirectoryItem>> = []
    const resultingTree = buildReposFileTree(fileTree, newKnownFolders, newKnownRootItems, sortDirectoryItems)
    initialRenderRootItems = resultingTree.newRootItems
  }
  hasDoneInitialRender.current = true
  const [knownFolders, dispatchKnownFolders] = React.useReducer(knownFolderReducer, newKnownFolders)
  const [rootItems, setRootItems] = React.useState<Array<TreeItem<DirectoryItem>>>(initialRenderRootItems)

  React.useEffect(() => {
    if (!showTree || !(!query || window.innerWidth >= ScreenSize.large)) {
      selectedElement.current = null
    }
  }, [showTree, query])

  React.useEffect(() => {
    if (openPanel && lastOpenPanel.current !== openPanel && window.innerWidth < TreeOverlayBreakpoint) {
      collapseTree({setPreference: false})
    }
    lastOpenPanel.current = openPanel
  }, [collapseTree, openPanel])

  // eslint-disable-next-line @eslint-react/no-unnecessary-use-callback
  const fetchFolder = React.useCallback(
    async (folderPath: string) => {
      const folder: DirectoryItem = {contentType: 'directory', path: folderPath, name: folderPath}
      const contentUrl = getItemUrl(folder)
      try {
        const folderPayload = await fetchTree(contentUrl)

        const folderData = {
          items: folderPayload.tree.items,
          totalCount: folderPayload.tree.totalCount,
        }

        // eslint-disable-next-line react-hooks/immutability
        fileTree[folderPath] = folderData
      } catch {
        setFetchError(true)
      }
      fetchedFolders.current.push(folderPath)
      pendingFetchCount.current -= 1
      if (pendingFetchCount.current === 0) {
        setTreeLoading(false)
      }
    },
    [fileTree, getItemUrl],
  )

  React.useEffect(() => {
    if (incompleteFileTree) {
      setTreeLoading(false)
      return
    }

    if (!foldersToFetch || foldersToFetch.length === 0) {
      return
    }

    // Only fetch folders we haven't fetched yet
    const foldersToFetchNow = foldersToFetch.filter(folder => !fetchedFolders.current.includes(folder))

    if (foldersToFetchNow.length > 0) {
      pendingFetchCount.current += foldersToFetchNow.length
      setTreeLoading(true)
      for (const folder of foldersToFetchNow) {
        fetchFolder(folder)
      }
    }
  }, [fetchFolder, foldersToFetch, incompleteFileTree])

  const focusActiveItem = React.useCallback(
    (selectedItemElement: HTMLElement | null) => {
      if (
        showTree &&
        (!queryRef.current || window.innerWidth >= ScreenSize.large) &&
        scrollingRef.current &&
        selectedItemElement
      ) {
        // On becoming visible, the tree should scroll to the selected item
        // Simulate "block: center" mode by adding an endMargin and startMargin of half the window height
        scrollIntoView(selectedItemElement, scrollingRef.current, {
          endMargin: window.innerHeight / 2,
          startMargin: window.innerHeight / 2,
          behavior: 'auto',
        })
      }
    },
    [showTree],
  )

  const setSelectedItemRef = React.useCallback(
    (selectedItemElement: HTMLElement | null) => {
      if (selectedItemElement && ignoreNextScroll.current) {
        ignoreNextScroll.current = false
      } else if (selectedElement.current !== selectedItemElement) {
        focusActiveItem(selectedItemElement)
      }
      selectedElement.current = selectedItemElement
    },
    [focusActiveItem],
  )

  const setScrollingRef = React.useCallback(
    (element: HTMLDivElement) => {
      scrollingRef.current = element
      const screenWidth = window.innerWidth
      // When the tree is a pane, focus after the scrollable container renders.
      if (screenWidth >= TreeOverlayBreakpoint) {
        focusActiveItem(selectedElement.current)
      }
    },
    [focusActiveItem],
  )

  const setOverlayRef = React.useCallback(
    (element: HTMLDivElement) => {
      // When the tree is in an overlay, we need to wait for the overlay container to mount
      // before focusing the active element.
      if (element) {
        focusActiveItem(selectedElement.current)
      }
    },
    [focusActiveItem],
  )

  const {screenSize} = useScreenSize()
  const showTreeOverlay =
    !isSSR &&
    ((openPanel && screenSize < TreeOverlayBreakpoint) || screenSize < ScreenSize.xlarge) &&
    screenSize >= ScreenSize.large

  const onTreeItemSelected = React.useCallback(() => {
    if (!showTreeOverlay) {
      onItemSelected()
    }
    ignoreNextScroll.current = true
  }, [onItemSelected, showTreeOverlay])

  const parentDirPath = isFilePath ? path.substring(0, path.lastIndexOf('/')) : path

  const fileTreeContextValue = useMemo(() => {
    return {knownFolders, dispatchKnownFolders}
  }, [knownFolders])

  // Create a portal node: this holds your rendered content
  const portalNode = React.useMemo(() => {
    if (isSSR) {
      return null
    }
    return createHtmlPortalNode()
  }, [isSSR])

  const exitOverlay = React.useCallback(() => {
    if (window.innerWidth > ScreenSize.large && window.innerWidth < ScreenSize.xxxxlarge) {
      collapseTree({setPreference: false})
    }
  }, [collapseTree])

  const onDefaultBranch = refInfo.name === defaultBranch

  /* on the server during SSR, the expanded value will purely be whatever their saved
  setting is, which might be expanded. On mobile widths we don't ever default to
  having the tree expanded, so on the server we need to just hard code it to
  show the regular not expanded version of everything*/
  const paneContents = (
    <div
      id={id}
      className={clsx(styles.PaneContents, isSSR && styles.PaneContentsHiddenOnNarrow, paneContentsClassName)}
    >
      <div className={clsx(headerClassName, styles.Box_1)}>
        {headerContent}
        {showRefSelectorRow && (
          <div className={styles.Box_2}>
            <div className={styles.Box_3}>
              {!onDefaultBranch && refInfo.fileExistsOnDefault !== undefined ? (
                <ButtonGroup className={styles.FullWidthButtonGroup}>
                  {refInfo.fileExistsOnDefault ? (
                    <IconButton
                      icon={ChevronLeftIcon}
                      aria-label="View file on default branch"
                      as={Link}
                      to={repositoryTreePath({repo, commitish: defaultBranch, action: 'blob', path})}
                    />
                  ) : (
                    <IconButton
                      icon={ChevronLeftIcon}
                      aria-label="View file on default branch"
                      description="File does not exist on default branch"
                      aria-disabled
                      inactive
                    />
                  )}
                  <ReposHeaderRefSelector
                    buttonClassName="react-repos-tree-pane-ref-selector width-full ref-selector-class"
                    allowResizing
                  />
                </ButtonGroup>
              ) : (
                <ReposHeaderRefSelector
                  buttonClassName="react-repos-tree-pane-ref-selector width-full ref-selector-class"
                  allowResizing
                />
              )}
            </div>
            <div className={styles.Box_4}>
              {refInfo.canEdit && (
                <IconButton
                  aria-label="Add file"
                  tooltipDirection="n"
                  as={Link}
                  icon={PlusIcon}
                  to={repositoryTreePath({repo, path: parentDirPath, commitish: refInfo.name, action: 'new'})}
                  // This callback will close the tree if this is a narrow viewport
                  onClick={onTreeItemSelected}
                  className={styles.IconButton}
                />
              )}
              <SearchButton
                className={refInfo.canEdit ? styles.SearchButtonWithLeftBorder : undefined}
                onClick={exitOverlay}
                textAreaId={textAreaId}
              />
            </div>
          </div>
        )}
      </div>

      {refInfo.currentOid && showFindFile && findFileWorkerPath && (
        <>
          <FileResultsList
            commitOid={refInfo.currentOid}
            findFileWorkerPath={findFileWorkerPath}
            onItemSelected={onItemSelected}
            searchBoxRef={searchBoxRef}
            className={clsx(styles.FileResultsList, isSSR && styles.PaneContentsHiddenOnNarrow)}
          />
          <FindFilesShortcut
            inputRef={searchBoxRef}
            onFindFilesShortcut={onFindFilesShortcut}
            textAreaId={textAreaId}
          />
        </>
      )}
      <TreeBorder scrollingRef={scrollingRef} />
      <div ref={setScrollingRef} className={clsx(styles.Box_5, isSSR && styles.PaneContentsHiddenOnNarrow)}>
        {isSSR
          ? refInfo.currentOid && (
              <div className={!query ? 'react-tree-show-tree-items' : 'react-tree-show-tree-items-on-large-screen'}>
                <ReposFileTreeView
                  clientOnlyFilePaths={clientOnlyFilePaths}
                  data={fileTree}
                  rootItems={rootItems}
                  selectedItemRef={setSelectedItemRef}
                  setRootItems={setRootItems}
                  onItemSelected={onTreeItemSelected}
                  processingTime={processingTime}
                  loading={treeLoading}
                  fetchError={fetchError}
                  incompleteFileTree={incompleteFileTree}
                  directoryNavigateOnClick={directoryNavigateOnClick}
                  getItemUrl={getTreeItemUrl}
                  className={treeContainerClassName}
                  getFileTrailingVisual={getFileTrailingVisual}
                  getFileIcon={getFileIcon}
                  sortDirectoryItems={sortDirectoryItems}
                  treeName={refInfo.name}
                />
              </div>
            )
          : portalNode && <OutPortal node={portalNode} />}
        {!refInfo.currentOid && !repo.isEmpty && (
          <div className={styles.Box_6}>
            <Octicon icon={AlertFillIcon} />
            &nbsp;Ref is invalid
          </div>
        )}
      </div>
    </div>
  )

  return (
    <FileTreeContext value={fileTreeContextValue}>
      {/* Render the TreeView in a portal that can be moved to different containers without rerendering */}
      {portalNode && (
        <InPortal node={portalNode}>
          {refInfo.currentOid && (
            <div className={!query ? 'react-tree-show-tree-items' : 'react-tree-show-tree-items-on-large-screen'}>
              <ReposFileTreeView
                clientOnlyFilePaths={clientOnlyFilePaths}
                data={fileTree}
                directoryNavigateOnClick={directoryNavigateOnClick}
                rootItems={rootItems}
                selectedItemRef={setSelectedItemRef}
                setRootItems={setRootItems}
                onItemSelected={onTreeItemSelected}
                processingTime={processingTime}
                loading={treeLoading}
                fetchError={fetchError}
                incompleteFileTree={incompleteFileTree}
                getItemUrl={getTreeItemUrl}
                className={treeContainerClassName}
                getFileTrailingVisual={getFileTrailingVisual}
                getFileIcon={getFileIcon}
                sortDirectoryItems={sortDirectoryItems}
                treeName={refInfo.name}
              />
            </div>
          )}
        </InPortal>
      )}
      {!showTree && codeCenterOption.enabled && treeToggleElement && (
        <div className={styles.Box_7}>{treeToggleElement}</div>
      )}
      <SplitPageLayout.Pane
        position="start"
        sticky
        padding="none"
        width="large"
        resizable={paneResizable ? true : false}
        widthStorageKey="codeView.tree-pane-width"
        divider={{regular: 'none', narrow: 'none'}}
        className={clsx(
          styles.Pane,
          !showTree && styles.HideTree,
          showTreeOverlay || openPanel ? styles.HidePaneWithTreeOverlay : styles.HidePane,
          paneClassName,
        )}
      >
        {showTree && !showTreeOverlay && (
          <div
            className={
              isSSR ? (openPanel ? 'react-tree-pane-contents-3-panel' : 'react-tree-pane-contents') : undefined
            }
          >
            {paneContents}
          </div>
        )}
      </SplitPageLayout.Pane>
      {showTree && showTreeOverlay && lastOpenPanel.current === openPanel && (
        <Overlay
          className={clsx(
            isSSR ? (openPanel ? 'react-tree-pane-overlay-3-panel' : 'react-tree-pane-overlay') : undefined,
            styles.Overlay,
            !showTree && styles.HideTree,
          )}
          ref={setOverlayRef}
          returnFocusRef={treeToggleRef}
          onClickOutside={exitOverlay}
          onEscape={exitOverlay}
          top={0}
          position="fixed"
        >
          <>{paneContents}</>
        </Overlay>
      )}
    </FileTreeContext>
  )
}

function knownFolderReducer(
  state: Map<string, TreeItem<DirectoryItem>>,
  action: {
    type: string
    folders: Map<string, TreeItem<DirectoryItem>>
    processingTime: number
  },
) {
  switch (action.type) {
    case 'set': {
      const stateInitialized = state?.size > 0
      sendEvent('file-tree', {
        'fetch-count': stateInitialized ? action.folders.size - state.size : action.folders.size,
        'file-count': action.folders.size,
        'nav-type': stateInitialized ? 'soft' : 'hard',
        'processing-time': action.processingTime,
      })
      return action.folders
    }
    case 'add': {
      const newState = new Map([...state, ...action.folders])
      sendEvent('file-tree', {
        'fetch-count': action.folders.size,
        'file-count': newState.size,
        'nav-type': 'fetch',
        'processing-time': action.processingTime,
      })
      return newState
    }
    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}

function TreeBorder({scrollingRef}: {scrollingRef: React.RefObject<HTMLDivElement | null>}) {
  // eslint-disable-next-line react-hooks/refs
  const [visible, setVisible] = React.useState(scrollingRef.current && scrollingRef.current.scrollTop > 0)

  React.useEffect(() => {
    if (scrollingRef.current) {
      const scrollElement = scrollingRef.current
      const scrollHandler = () => {
        if (scrollElement && scrollElement.scrollTop > 0) {
          setVisible(true)
        } else {
          setVisible(false)
        }
      }
      // eslint-disable-next-line github/prefer-observers
      scrollElement.addEventListener('scroll', scrollHandler)
      return () => {
        scrollElement.removeEventListener('scroll', scrollHandler)
      }
    }
  }, [scrollingRef])

  return visible ? <div className={styles.Box_8} /> : null
}
